import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Clock3,
  IndianRupee,
  MapPin,
  Search,
  X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { STATUS_BEGIN, STATUS_DONE, STATUS_FAIL } from "utils/constants/others";
import usePlanShipmentStore from "./usePlanShipmentStore";
import ShippingNotesList from "./ShippingNotesList";
import {
  canSchedulePO,
  formatInvoiceAmount,
  formatPurchaseOrderCount,
  formatScheduleDate,
  formatScheduleGroupNumber,
  formatSKUCount,
  formatSlotDisplayName,
  formatStatus,
  formatWeight,
  getMinDaysFromConfig,
  getSchedulingEnabledDate,
} from "./planShipmentUtils";
import {
  AllCaughtUpIcon,
  AllCaughtUpState,
  AllCaughtUpTitle,
  CardHeader,
  CardSubtitle,
  CardTitle,
  EmptyState,
  ErrorMessage,
  HeaderActions,
  HeaderIconButton,
  HeaderTitle,
  HelpSheetBackdrop,
  HelpSheetBody,
  HelpSheetCloseButton,
  HelpSheetHeader,
  HelpSheetModal,
  HelpSheetTitle,
  MetadataGrid,
  MetadataIcon,
  MetadataItem,
  MetadataValue,
  PageContent,
  PageFrame,
  PageHeader,
  PageShell,
  PrimaryTab,
  PrimaryTabs,
  RetryButton,
  ScheduleArrow,
  ScheduleCard,
  ScheduleCards,
  ScheduleStatus,
  ScheduleTab,
  ScheduleTabs,
  SearchBar,
  SearchInput,
  SkeletonCard,
  SkeletonLine,
  StateTitle,
} from "./styles.planShipment";

const PAGE_SIZE = 20;
const SCHEDULE_TABS = {
  unscheduled: "unscheduled",
  scheduled: "scheduled",
};
const PRIMARY_TABS = {
  poSchedules: "po_schedules",
  shippingNotes: "shipping_notes",
};

const PurchaseOrderCard = ({ poItem, onOpen, minDays }) => {
  const isSchedulable = canSchedulePO(poItem.expected_delivery_date, minDays);

  return (
    <ScheduleCard
      $disabled={!isSchedulable}
      onClick={isSchedulable ? onOpen : undefined}
      style={isSchedulable ? { cursor: "pointer" } : undefined}
    >
      <ScheduleStatus $tone="unscheduled">
        {formatStatus(poItem.derived_status)}
      </ScheduleStatus>
      <CardHeader>
        <div>
          <CardTitle>{poItem.purchase_order_number || "—"}</CardTitle>
          <CardSubtitle>
            {formatSKUCount(poItem.total_skus)}
            {" • "}
            {formatWeight(poItem.total_weight)}
          </CardSubtitle>
        </div>
        {isSchedulable && (
          <ScheduleArrow
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            aria-label={`Open ${poItem.purchase_order_number || "purchase order"}`}
          >
            <ChevronRight aria-hidden="true" size={27} strokeWidth={1.8} />
          </ScheduleArrow>
        )}
      </CardHeader>

      <MetadataGrid $disabled={!isSchedulable}>
        <MetadataItem>
          <MetadataIcon>
            <CalendarDays aria-hidden="true" />
          </MetadataIcon>
          <MetadataValue>
            {formatScheduleDate(poItem.expected_delivery_date)}
          </MetadataValue>
        </MetadataItem>
        <MetadataItem>
          <MetadataIcon>
            <MapPin aria-hidden="true" />
          </MetadataIcon>
          <MetadataValue>
            {(() => {
              const raw = poItem.warehouse_code || "";
              const commaIndex = raw.indexOf(",");
              if (!raw) return "—";
              if (commaIndex === -1) return raw;
              const code = raw.slice(0, commaIndex).trim();
              const city = raw.slice(commaIndex + 1).trim();
              return (
                <>
                  {code}
                  {city ? <><br />{city}</> : null}
                </>
              );
            })()}
          </MetadataValue>
        </MetadataItem>
        <MetadataItem>
          <MetadataIcon>
            <IndianRupee aria-hidden="true" />
          </MetadataIcon>
          <MetadataValue>{formatInvoiceAmount(poItem.po_amount)}</MetadataValue>
        </MetadataItem>
      </MetadataGrid>

      {!isSchedulable && (
        <div style={{ padding: "10px 0 2px", textAlign: "center", color: "#293142", fontSize: "12px", fontFamily: '"OurLexend-Regular", Helvetica, sans-serif' }}>
          Scheduling will be enabled after {getSchedulingEnabledDate(poItem.expected_delivery_date, minDays)}
        </div>
      )}
    </ScheduleCard>
  );
};

const ScheduleGroupCard = ({ scheduleGroup, onOpen }) => (
  <ScheduleCard onClick={onOpen} style={{ cursor: "pointer" }}>
    <ScheduleStatus>{formatStatus(scheduleGroup.status)}</ScheduleStatus>
    <CardHeader>
      <div>
        <CardTitle>{formatScheduleGroupNumber(scheduleGroup)}</CardTitle>
        <CardSubtitle>
          {formatPurchaseOrderCount(scheduleGroup.purchase_order_count)}
          {" • "}
          {formatWeight(scheduleGroup.total_weight_in_kg)}
        </CardSubtitle>
      </div>
      <ScheduleArrow
        type="button"
        onClick={(e) => { e.stopPropagation(); onOpen(); }}
        aria-label={`Open ${formatScheduleGroupNumber(scheduleGroup)}`}
      >
        <ChevronRight aria-hidden="true" size={27} strokeWidth={1.8} />
      </ScheduleArrow>
    </CardHeader>

    <MetadataGrid>
      <MetadataItem>
        <MetadataIcon>
          <CalendarDays aria-hidden="true" />
        </MetadataIcon>
        <MetadataValue>
          {formatScheduleDate(scheduleGroup.schedule_date)}
        </MetadataValue>
      </MetadataItem>
      <MetadataItem>
        <MetadataIcon>
          <Clock3 aria-hidden="true" />
        </MetadataIcon>
        <MetadataValue>
          {formatSlotDisplayName(scheduleGroup.slot)}
        </MetadataValue>
      </MetadataItem>
      <MetadataItem>
          <MetadataIcon>
            <MapPin aria-hidden="true" />
          </MetadataIcon>
          <MetadataValue>
            {(() => {
              const raw = scheduleGroup.warehouse_name || "";
              const commaIndex = raw.indexOf(",");
              if (!raw) return "—";
              if (commaIndex === -1) return raw;
              const code = raw.slice(0, commaIndex).trim();
              const city = raw.slice(commaIndex + 1).trim();
              return (
                <>
                  {code}
                  {city ? <><br />{city}</> : null}
                </>
              );
            })()}
          </MetadataValue>
        </MetadataItem>
    </MetadataGrid>
  </ScheduleCard>
);

const ScheduleListSkeleton = () => (
  <ScheduleCards aria-label="Loading scheduled PO groups">
    {[0, 1, 2].map((item) => (
      <SkeletonCard key={item}>
        <SkeletonLine $width="96px" />
        <SkeletonLine $width="148px" $height="22px" />
        <SkeletonLine $width="118px" />
        <SkeletonLine $height="82px" />
      </SkeletonCard>
    ))}
  </ScheduleCards>
);

const PlanShipmentMobile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activePrimaryTab, setActivePrimaryTab] = useState(
    location.state?.activeTab === "shipping_notes"
      ? PRIMARY_TABS.shippingNotes
      : PRIMARY_TABS.poSchedules
  );

  // Clear the navigation state once consumed so a page reload doesn't
  // re-apply the tab from the (still-persisted) history entry state.
  useEffect(() => {
    if (location.state?.activeTab || location.state?.activeScheduleTab) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []);
  const [activeScheduleTab, setActiveScheduleTab] = useState(
    location.state?.activeScheduleTab === SCHEDULE_TABS.scheduled
      ? SCHEDULE_TABS.scheduled
      : SCHEDULE_TABS.unscheduled
  );
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const {
    getPOScheduleGroups,
    getPOScheduleGroupsStatus,
    poScheduleGroups,
    poScheduleGroupsError,
    poScheduleGroupsHasMore,
    loadMorePOScheduleGroups,
    searchPOScheduleGroups,
    clearShippingNoteSelection,
    getPurchaseOrders,
    getPurchaseOrdersStatus,
    purchaseOrders,
    purchaseOrdersError,
    purchaseOrdersHasMore,
    loadMorePurchaseOrders,
    searchPurchaseOrders,
    getPlanShipmentHelpSections,
    planShipmentHelpSections,
    planShipmentAsnConfig,
  } = usePlanShipmentStore();

  const minDays = getMinDaysFromConfig(planShipmentAsnConfig);

  const [showHelpSheet, setShowHelpSheet] = useState(false);

  const unscheduledSentinelRef = useRef(null);
  const scheduledSentinelRef = useRef(null);

  const vendorId = window.localStorage.getItem("outlet_id");
  const hasValidVendorId =
    Number.isInteger(Number(vendorId)) && Number(vendorId) > 0;

  const fetchScheduleGroups = useCallback(() => {
    if (!hasValidVendorId) return;

    getPOScheduleGroups({
      vendor_id: vendorId,
      page_size: PAGE_SIZE,
      page_number: 1,
    });
  }, [getPOScheduleGroups, hasValidVendorId, vendorId]);

  const fetchUnscheduledPOs = useCallback(() => {
    if (!hasValidVendorId) return;

    getPurchaseOrders({
      vendor_id: vendorId,
      page_number: 1,
      page_size: PAGE_SIZE,
    });
  }, [getPurchaseOrders, hasValidVendorId, vendorId]);

  const isScheduledTabActive = activeScheduleTab === SCHEDULE_TABS.scheduled;
  const isUnscheduledTabActive = activeScheduleTab === SCHEDULE_TABS.unscheduled;
  const isPOSchedulesActive = activePrimaryTab === PRIMARY_TABS.poSchedules;
  const trimmedSearchQuery = searchQuery.trim();

  useEffect(() => {
    clearShippingNoteSelection();
    if (hasValidVendorId) {
      getPlanShipmentHelpSections({ seller_outlet_id: vendorId });
    }
  }, [clearShippingNoteSelection, getPlanShipmentHelpSections, hasValidVendorId, vendorId]);

  useEffect(() => {
    if (!isPOSchedulesActive || !isUnscheduledTabActive) return;

    if (!trimmedSearchQuery) {
      fetchUnscheduledPOs();
      return;
    }

    const timeoutId = setTimeout(() => {
      searchPurchaseOrders({
        vendor_id: vendorId,
        purchase_order_id: Number(trimmedSearchQuery),
      });
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [
    isPOSchedulesActive,
    isUnscheduledTabActive,
    trimmedSearchQuery,
    fetchUnscheduledPOs,
    searchPurchaseOrders,
    vendorId,
  ]);

  useEffect(() => {
    if (!isPOSchedulesActive || !isScheduledTabActive) return;

    if (!trimmedSearchQuery) {
      fetchScheduleGroups();
      return;
    }

    const timeoutId = setTimeout(() => {
      searchPOScheduleGroups({
        vendor_id: vendorId,
        schedule_group_id: Number(trimmedSearchQuery),
      });
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [
    isPOSchedulesActive,
    isScheduledTabActive,
    trimmedSearchQuery,
    fetchScheduleGroups,
    searchPOScheduleGroups,
    vendorId,
  ]);

  // Infinite scroll: load next page when sentinel enters viewport
  useEffect(() => {
    const sentinel = unscheduledSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMorePurchaseOrders(); },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMorePurchaseOrders, isUnscheduledTabActive, purchaseOrdersHasMore, purchaseOrders.length]);

  useEffect(() => {
    const sentinel = scheduledSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMorePOScheduleGroups(); },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMorePOScheduleGroups, isScheduledTabActive, poScheduleGroupsHasMore, poScheduleGroups.length]);

  const isScheduledLoading =
    (getPOScheduleGroupsStatus === STATUS_BEGIN || !getPOScheduleGroupsStatus) &&
    poScheduleGroups.length === 0;
  const isUnscheduledLoading =
    (getPurchaseOrdersStatus === STATUS_BEGIN || !getPurchaseOrdersStatus) &&
    purchaseOrders.length === 0;

  const asnHelpImage =
    planShipmentHelpSections.find((section) => section?.value === "ASN")?.cta
      ?.action_data?.image_url || "";

  return (
    <PageShell>
      <PageFrame>
        <PageHeader>
          <HeaderIconButton
            type="button"
            onClick={() => navigate("/")}
            aria-label="Go back"
          >
            <ChevronLeft aria-hidden="true" />
          </HeaderIconButton>
          <HeaderTitle>Plan shipment</HeaderTitle>
          <HeaderActions>
            <HeaderIconButton
              type="button"
              disabled={!asnHelpImage}
              onClick={() => setShowHelpSheet(true)}
              aria-label={
                asnHelpImage ? "View help" : "Help will be available soon"
              }
            >
              <CircleHelp aria-hidden="true" />
            </HeaderIconButton>
            <HeaderIconButton
              type="button"
              onClick={() => setShowSearch((currentValue) => !currentValue)}
              aria-label={
                showSearch
                  ? "Close search"
                  : isPOSchedulesActive
                  ? "Search scheduled shipments"
                  : "Search shipping notes"
              }
              aria-expanded={showSearch}
            >
              {showSearch ? (
                <X aria-hidden="true" />
              ) : (
                <Search aria-hidden="true" />
              )}
            </HeaderIconButton>
          </HeaderActions>
        </PageHeader>

        {showSearch ? (
          <SearchBar>
            <Search aria-hidden="true" />
            <SearchInput
              autoFocus
              inputMode="numeric"
              value={searchQuery}
              onChange={(event) =>
                setSearchQuery(event.target.value.replace(/\D/g, ""))
              }
              placeholder={
                isPOSchedulesActive
                  ? isUnscheduledTabActive
                    ? "Search by PO ID"
                    : "Search by schedule group ID"
                  : "Search by ASN ID"
              }
              aria-label={
                isPOSchedulesActive
                  ? isUnscheduledTabActive
                    ? "Search by PO ID"
                    : "Search by schedule group ID"
                  : "Search by ASN ID"
              }
            />
          </SearchBar>
        ) : null}

        <PrimaryTabs>
          <PrimaryTab
            type="button"
            $active={isPOSchedulesActive}
            aria-current={isPOSchedulesActive ? "page" : undefined}
            onClick={() => {
              setActivePrimaryTab(PRIMARY_TABS.poSchedules);
              setSearchQuery("");
            }}
          >
            PO schedules
          </PrimaryTab>
          <PrimaryTab
            type="button"
            $active={!isPOSchedulesActive}
            aria-current={!isPOSchedulesActive ? "page" : undefined}
            onClick={() => {
              setActivePrimaryTab(PRIMARY_TABS.shippingNotes);
              setSearchQuery("");
            }}
          >
            Shipping notes
          </PrimaryTab>
        </PrimaryTabs>

        <PageContent>
          {!isPOSchedulesActive ? (
            <ShippingNotesList searchQuery={searchQuery} />
          ) : null}

          {isPOSchedulesActive ? (
            <>
              <ScheduleTabs aria-label="PO schedule status">
                <ScheduleTab
                  type="button"
                  $active={isUnscheduledTabActive}
                  onClick={() =>
                    setActiveScheduleTab(SCHEDULE_TABS.unscheduled)
                  }
                >
                  Unscheduled
                </ScheduleTab>
                <ScheduleTab
                  type="button"
                  $active={isScheduledTabActive}
                  onClick={() => setActiveScheduleTab(SCHEDULE_TABS.scheduled)}
                >
                  Scheduled
                </ScheduleTab>
              </ScheduleTabs>

              {/* ── Unscheduled tab ── */}
              {isUnscheduledTabActive && !hasValidVendorId ? (
                <EmptyState>
                  <StateTitle>Unable to load purchase orders</StateTitle>
                  <ErrorMessage>
                    Please select a valid outlet and try again.
                  </ErrorMessage>
                </EmptyState>
              ) : null}

              {isUnscheduledTabActive && hasValidVendorId && isUnscheduledLoading ? (
                <ScheduleListSkeleton />
              ) : null}

              {isUnscheduledTabActive &&
              hasValidVendorId &&
              getPurchaseOrdersStatus === STATUS_FAIL ? (
                <EmptyState>
                  <StateTitle>Unable to load purchase orders</StateTitle>
                  <ErrorMessage>{purchaseOrdersError}</ErrorMessage>
                  <RetryButton type="button" onClick={fetchUnscheduledPOs}>
                    Try again
                  </RetryButton>
                </EmptyState>
              ) : null}

              {isUnscheduledTabActive &&
              getPurchaseOrdersStatus === STATUS_DONE &&
              purchaseOrders.length === 0 ? (
                searchQuery ? (
                  <EmptyState>
                    <StateTitle>No matching purchase orders</StateTitle>
                    <span>Try searching with another PO ID.</span>
                  </EmptyState>
                ) : (
                  <AllCaughtUpState>
                    <AllCaughtUpIcon>
                      <img src="/logos/all_caught_up.svg" alt="" aria-hidden="true" />
                    </AllCaughtUpIcon>
                    <AllCaughtUpTitle>All caught up!</AllCaughtUpTitle>
                    <span>There are no purchase orders to schedule.</span>
                  </AllCaughtUpState>
                )
              ) : null}

              {isUnscheduledTabActive &&
              purchaseOrders.length > 0 ? (
                <ScheduleCards>
                  {purchaseOrders.map((poItem) => (
                    <PurchaseOrderCard
                      key={poItem.id}
                      poItem={poItem}
                      minDays={minDays}
                      onOpen={() =>
                        navigate(
                          `/plan-shipment/purchase-orders/${poItem.id}`,
                          { state: { primaryPoData: poItem } }
                        )
                      }
                    />
                  ))}
                  {purchaseOrdersHasMore ? (
                    <div ref={unscheduledSentinelRef} style={{ height: 1 }} />
                  ) : null}
                </ScheduleCards>
              ) : null}

              {/* ── Scheduled tab ── */}
              {isScheduledTabActive && !hasValidVendorId ? (
                <EmptyState>
                  <StateTitle>Unable to load schedules</StateTitle>
                  <ErrorMessage>
                    Please select a valid outlet and try again.
                  </ErrorMessage>
                </EmptyState>
              ) : null}

              {isScheduledTabActive && hasValidVendorId && isScheduledLoading ? (
                <ScheduleListSkeleton />
              ) : null}

              {isScheduledTabActive &&
              hasValidVendorId &&
              getPOScheduleGroupsStatus === STATUS_FAIL ? (
                <EmptyState>
                  <StateTitle>Unable to load schedules</StateTitle>
                  <ErrorMessage>{poScheduleGroupsError}</ErrorMessage>
                  <RetryButton type="button" onClick={fetchScheduleGroups}>
                    Try again
                  </RetryButton>
                </EmptyState>
              ) : null}

              {isScheduledTabActive &&
              getPOScheduleGroupsStatus === STATUS_DONE &&
              poScheduleGroups.length === 0 ? (
                searchQuery ? (
                  <EmptyState>
                    <StateTitle>No matching schedules</StateTitle>
                    <span>Try searching with another schedule group ID.</span>
                  </EmptyState>
                ) : (
                  <AllCaughtUpState>
                    <AllCaughtUpIcon>
                      <img src="/logos/all_caught_up.svg" alt="" aria-hidden="true" />
                    </AllCaughtUpIcon>
                    <AllCaughtUpTitle>All caught up!</AllCaughtUpTitle>
                    <span>There are no schedule groups.</span>
                  </AllCaughtUpState>
                )
              ) : null}

              {isScheduledTabActive &&
              poScheduleGroups.length > 0 ? (
                <ScheduleCards>
                  {poScheduleGroups.map((scheduleGroup) => (
                    <ScheduleGroupCard
                      key={scheduleGroup.schedule_group_id}
                      scheduleGroup={scheduleGroup}
                      onOpen={() =>
                        navigate(
                          `/plan-shipment/schedule-groups/${scheduleGroup.schedule_group_id}`
                        )
                      }
                    />
                  ))}
                  {poScheduleGroupsHasMore ? (
                    <div ref={scheduledSentinelRef} style={{ height: 1 }} />
                  ) : null}
                </ScheduleCards>
              ) : null}
            </>
          ) : null}
        </PageContent>
      </PageFrame>

      {showHelpSheet && asnHelpImage ? (
        <HelpSheetBackdrop onClick={() => setShowHelpSheet(false)}>
          <HelpSheetModal
            role="dialog"
            aria-modal="true"
            aria-labelledby="plan-shipment-help-title"
            onClick={(event) => event.stopPropagation()}
          >
            <HelpSheetHeader>
              <HelpSheetTitle id="plan-shipment-help-title">
                Help
              </HelpSheetTitle>
              <HelpSheetCloseButton
                type="button"
                onClick={() => setShowHelpSheet(false)}
                aria-label="Close help"
              >
                <X aria-hidden="true" />
              </HelpSheetCloseButton>
            </HelpSheetHeader>
            <HelpSheetBody>
              <img src={asnHelpImage} alt="Shipping note help guide" />
            </HelpSheetBody>
          </HelpSheetModal>
        </HelpSheetBackdrop>
      ) : null}
    </PageShell>
  );
};

export default PlanShipmentMobile;
